import { useEffect, useRef } from "react";
import Swal from "sweetalert2";
import { BUILD_VERSION } from "../buildVersion";

/**
 * Checks the deployed version.json against the version baked into the bundle.
 * If they differ, clears caches/service workers and force-reloads the page so
 * users always get the latest deployment instead of a stale cached build.
 */

const VERSION_URL = `${process.env.PUBLIC_URL || ""}/version.json`;
const POLL_INTERVAL_MS = 2 * 60 * 1000; // every 2 minutes
/** Retries when the first reload still returns a cached bundle (sessionStorage survives reload). */
const RELOAD_ATTEMPTS_KEY = "__pmr_vc_reload_attempts__";
const MAX_RELOAD_ATTEMPTS = 10;
const POST_RELOAD_ALERT_FLAG = "__pmr_cache_cleared_alert__";

async function fetchRemoteVersion() {
  try {
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
      cache: "no-store",
      headers: {
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
      },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data && data.version ? String(data.version) : null;
  } catch (e) {
    return null;
  }
}

async function clearAllCaches() {
  try {
    if ("caches" in window) {
      const keys = await window.caches.keys();
      await Promise.all(keys.map((k) => window.caches.delete(k)));
    }
  } catch (e) {
    // ignore
  }
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(
        regs.map((registration) => {
          const scriptUrl =
            registration.active?.scriptURL ||
            registration.installing?.scriptURL ||
            registration.waiting?.scriptURL ||
            "";
          if (scriptUrl.endsWith("/sw.js")) return Promise.resolve();
          return registration.unregister();
        })
      );
    }
  } catch (e) {
    // ignore
  }
}

function forceReload() {
  try {
    const raw = sessionStorage.getItem(RELOAD_ATTEMPTS_KEY);
    const n = raw ? parseInt(raw, 10) : 0;
    if (n >= MAX_RELOAD_ATTEMPTS) {
      console.error(
        "[VersionChecker] Too many reload attempts; clear site data or hard-refresh (Ctrl+Shift+R)."
      );
      return;
    }
    sessionStorage.setItem(RELOAD_ATTEMPTS_KEY, String(n + 1));
  } catch (e) {
    // ignore
  }

  const url = new URL(window.location.href);
  url.searchParams.set("v", Date.now().toString());
  window.location.replace(url.toString());
}

export async function checkVersionOnce() {
  if (BUILD_VERSION === "dev") return false; // skip in dev
  const remote = await fetchRemoteVersion();
  if (!remote) return false;
  if (remote !== BUILD_VERSION) {
    console.warn(
      `[VersionChecker] New build detected. local=${BUILD_VERSION} remote=${remote}. Reloading...`
    );
    await clearAllCaches();

    try {
      sessionStorage.setItem(POST_RELOAD_ALERT_FLAG, "1");
    } catch (e) {
      // ignore
    }

    try {
      await Swal.fire({
        icon: "info",
        title: "New version available",
        html:
          "Cache has been cleared automatically.<br/>" +
          "The page will now reload to load the latest build.",
        confirmButtonText: "Reload now",
        allowOutsideClick: false,
        allowEscapeKey: false,
      });
    } catch (e) {
      // ignore
    }

    forceReload();
    return true;
  }
  try {
    sessionStorage.removeItem(RELOAD_ATTEMPTS_KEY);
  } catch (e) {
    // ignore
  }
  return false;
}

/**
 * React hook: starts version polling on mount.
 * Re-checks on tab focus and at a fixed interval.
 */
export function useVersionChecker(intervalMs = POLL_INTERVAL_MS) {
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    // After a forced reload, confirm to the user that cache was cleared.
    try {
      if (sessionStorage.getItem(POST_RELOAD_ALERT_FLAG) === "1") {
        sessionStorage.removeItem(POST_RELOAD_ALERT_FLAG);
        Swal.fire({
          icon: "success",
          title: "Cache cleared",
          text: "Cache cleared successfully. You are now on the latest version.",
          confirmButtonText: "OK",
          timer: 4000,
          timerProgressBar: true,
        });
      }
    } catch (e) {
      // ignore
    }

    checkVersionOnce();

    const interval = setInterval(checkVersionOnce, intervalMs);

    const onFocus = () => checkVersionOnce();
    const onVisibility = () => {
      if (document.visibilityState === "visible") checkVersionOnce();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [intervalMs]);
}
